/* BeadznThreadz — content page behaviours: table of contents, reading progress, share/copy, FAQ filter, Copy as Markdown. */
(function () {
  if (window.__bzPagesInit) return;
  window.__bzPagesInit = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const toast = (m) => window.BZ && window.BZ.toast(m);

  function slugify(text) {
    return (
      text
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60) || 'section'
    );
  }

  /* ---------- Table of contents ---------- */
  function initToc(scope) {
    scope.querySelectorAll('[data-bz-toc]').forEach((list) => {
      if (list.dataset.ready) return;
      list.dataset.ready = 'true';
      const container = list.closest('.bz-section') || document;
      const source = container.querySelector('[data-bz-toc-source]');
      const wrap = list.closest('[data-bz-toc-wrap]');
      if (!source) return;
      const levels = (list.dataset.levels || 'h2').split(',');
      const headings = [...source.querySelectorAll(levels.join(','))].filter((h) => h.textContent.trim());
      if (headings.length < 2) return;
      const used = new Set();
      headings.forEach((h) => {
        let id = h.id || slugify(h.textContent);
        while (used.has(id) || (document.getElementById(id) && document.getElementById(id) !== h)) id += '-1';
        used.add(id);
        h.id = id;
        h.classList.add('bz-anchor');
        const li = document.createElement('li');
        li.className = 'bz-toc__item bz-toc__item--' + h.tagName.toLowerCase();
        const a = document.createElement('a');
        a.href = '#' + id;
        a.textContent = h.textContent.trim();
        li.appendChild(a);
        list.appendChild(li);
      });
      if (wrap) wrap.hidden = false;

      if ('IntersectionObserver' in window) {
        const links = new Map([...list.querySelectorAll('a')].map((a) => [a.getAttribute('href').slice(1), a]));
        let active;
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => {
              if (!e.isIntersecting) return;
              const link = links.get(e.target.id);
              if (!link || link === active) return;
              if (active) active.removeAttribute('aria-current');
              link.setAttribute('aria-current', 'true');
              active = link;
            });
          },
          { rootMargin: '-20% 0px -70% 0px' }
        );
        headings.forEach((h) => io.observe(h));
      }
    });
  }

  /* ---------- Reading progress ---------- */
  function initProgress() {
    const bar = document.querySelector('[data-bz-progress]');
    const article = document.querySelector('[data-bz-toc-source]');
    if (!bar || !article) return;
    let ticking = false;
    const update = () => {
      const rect = article.getBoundingClientRect();
      const total = rect.height - window.innerHeight * 0.6;
      const done = Math.min(Math.max(-rect.top + window.innerHeight * 0.2, 0), Math.max(total, 1));
      bar.style.transform = `scaleX(${(done / Math.max(total, 1)).toFixed(4)})`;
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  }

  /* ---------- Copy helpers ---------- */
  async function copyText(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      let ok = false;
      try {
        ok = document.execCommand('copy');
      } catch (err) {}
      ta.remove();
      return ok;
    }
  }

  function toMarkdown(node) {
    const walk = (el, depth) => {
      let out = '';
      el.childNodes.forEach((n) => {
        if (n.nodeType === 3) {
          out += n.textContent.replace(/\s+/g, ' ');
          return;
        }
        if (n.nodeType !== 1) return;
        const tag = n.tagName.toLowerCase();
        const inner = () => walk(n, depth).trim();
        switch (tag) {
          case 'h1': out += `\n# ${inner()}\n\n`; break;
          case 'h2': out += `\n## ${inner()}\n\n`; break;
          case 'h3': out += `\n### ${inner()}\n\n`; break;
          case 'h4': out += `\n#### ${inner()}\n\n`; break;
          case 'p': out += `${inner()}\n\n`; break;
          case 'br': out += '\n'; break;
          case 'strong': case 'b': out += `**${inner()}**`; break;
          case 'em': case 'i': out += `_${inner()}_`; break;
          case 'small': out += inner(); break;
          case 'a': out += `[${inner()}](${n.href})`; break;
          case 'blockquote': out += inner().split('\n').map((l) => `> ${l}`).join('\n') + '\n\n'; break;
          case 'ul': case 'ol': {
            let i = 0;
            n.childNodes.forEach((li) => {
              if (li.nodeType !== 1 || li.tagName.toLowerCase() !== 'li') return;
              i++;
              const bullet = tag === 'ol' ? `${i}.` : '-';
              const nested = [...li.children].filter((c) => /^(ul|ol)$/i.test(c.tagName));
              const clone = li.cloneNode(true);
              clone.querySelectorAll(':scope > ul, :scope > ol').forEach((c) => c.remove());
              out += `${'  '.repeat(depth)}${bullet} ${walk(clone, depth).trim()}\n`;
              nested.forEach((nl) => {
                const wrapper = document.createElement('div');
                wrapper.appendChild(nl.cloneNode(true));
                out += walk(wrapper, depth + 1);
              });
            });
            if (depth === 0) out += '\n';
            break;
          }
          default: out += walk(n, depth);
        }
      });
      return out;
    };
    return walk(node, 0).replace(/\n{3,}/g, '\n\n').trim() + '\n';
  }

  document.addEventListener('click', async (e) => {
    const linkBtn = e.target.closest('[data-bz-copy]');
    if (linkBtn) {
      e.preventDefault();
      if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
        try {
          await navigator.share({ title: document.title, url: linkBtn.dataset.bzCopy });
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return;
        }
      }
      if (await copyText(linkBtn.dataset.bzCopy)) toast(linkBtn.dataset.bzCopied || 'Copied');
      return;
    }
    const mdBtn = e.target.closest('[data-bz-copy-md]');
    if (mdBtn) {
      const doc = document.querySelector(mdBtn.dataset.bzCopyMd);
      if (doc && (await copyText(toMarkdown(doc)))) toast(mdBtn.dataset.bzCopied || 'Copied');
    }
  });

  /* ---------- FAQ ---------- */
  function initFaq(scope) {
    scope.querySelectorAll('[data-bz-faq]').forEach((faq) => {
      if (faq.dataset.ready) return;
      faq.dataset.ready = 'true';
      const items = [...faq.querySelectorAll('[data-bz-faq-item]')];
      const tabs = [...faq.querySelectorAll('[data-bz-faq-tab]')];
      const search = faq.querySelector('[data-bz-faq-search]');
      const empty = faq.querySelector('[data-bz-faq-empty]');
      let cat = 'all';

      const apply = () => {
        const q = (search ? search.value : '').trim().toLowerCase();
        let shown = 0;
        items.forEach((item) => {
          const matchCat = cat === 'all' || item.dataset.cat === cat;
          const matchQ = !q || item.textContent.toLowerCase().includes(q);
          const show = matchCat && matchQ;
          item.hidden = !show;
          if (show) shown++;
          if (q && show) item.open = true;
        });
        if (empty) empty.hidden = shown > 0;
      };

      tabs.forEach((tab) =>
        tab.addEventListener('click', () => {
          cat = tab.dataset.bzFaqTab;
          tabs.forEach((t) => {
            const on = t === tab;
            t.classList.toggle('is-active', on);
            t.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          apply();
        })
      );
      if (search) {
        let timer;
        search.addEventListener('input', () => {
          clearTimeout(timer);
          timer = setTimeout(apply, 120);
        });
      }

      // Smooth open/close
      items.forEach((d) => {
        const summary = d.querySelector('summary');
        const body = d.querySelector('.bz-acc__a');
        if (!summary || !body || reduceMotion) return;
        summary.addEventListener('click', (ev) => {
          ev.preventDefault();
          if (d.open) {
            const h = body.scrollHeight;
            body.animate([{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 260, easing: 'ease-out' }).onfinish = () => (d.open = false);
          } else {
            d.open = true;
            const h = body.scrollHeight;
            body.animate([{ height: '0px', opacity: 0 }, { height: h + 'px', opacity: 1 }], { duration: 320, easing: 'ease-out' });
          }
        });
      });

      // Deep link: #faq-question-slug opens that question
      if (location.hash) {
        const target = document.getElementById(location.hash.slice(1));
        if (target && target.matches('[data-bz-faq-item]')) target.open = true;
      }
    });
  }

  function init(scope) {
    initToc(scope || document);
    initFaq(scope || document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init(document);
      initProgress();
    });
  } else {
    init(document);
    initProgress();
  }
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
